import { createFileRoute, Link } from "@tanstack/react-router";
import { TableLoaderRow } from "@/components/ui/loader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDepRuns } from "@/features/finance/api";
import { DepStatusBadge } from "@/features/finance/badges";
import { Plus } from "lucide-react";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canRunDepreciation } from "@/lib/permissions";

export const Route = createFileRoute("/_authenticated/finance/depreciation")({
  component: DepRunsList,
});

function DepRunsList() {
  const { data, isLoading } = useDepRuns();
  const { data: me } = useCurrentUser();
  const canCreate = canRunDepreciation(me?.roles ?? []);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Depreciation Runs</h1> <ReadOnlyPill module="finance" />
          <p className="text-sm text-muted-foreground">Preview, confirm and lock depreciation per FY.</p>
        </div>
        <ActionGuard can={canCreate}>
          <Button asChild><Link to="/finance/depreciation/new"><Plus className="mr-1 h-4 w-4" />New Run</Link></Button>
        </ActionGuard>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">All runs</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>FY</TableHead><TableHead>Period</TableHead>
              <TableHead>Method</TableHead><TableHead>Status</TableHead><TableHead>Run Date</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading && <TableLoaderRow colSpan={6} />}
              {(data ?? []).map((r) => (
                <TableRow key={r.id} className="cursor-pointer">
                  <TableCell><Link to="/finance/depreciation/$runId" params={{ runId: r.id }} className="text-primary hover:underline">{r.run_name}</Link></TableCell>
                  <TableCell>{r.fy}</TableCell>
                  <TableCell>{r.period}</TableCell>
                  <TableCell>{r.method}</TableCell>
                  <TableCell><DepStatusBadge status={r.status} /></TableCell>
                  <TableCell>{r.run_date}</TableCell>
                </TableRow>
              ))}
              {!isLoading && (data ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No runs yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
